import { useEffect, useRef, useState } from "react";
import CountUp from "./CountUp";
import { stats } from "./StatFlipCards";

/** PREVIEW VARIANT (?stats=four): "By the numbers" as four cards that flip into view one
 *  after another, then take turns flipping. Marketing is comparing this against the
 *  single flipping card and the original layout. Static for reduced motion. */
const StatFlipGrid = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  // full turns per card; incrementing one spins that card 360° (no reset needed)
  const [turns, setTurns] = useState(() => stats.map(() => 0));

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShown(true);
          obs.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!shown || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let i = 0;
    const id = window.setInterval(() => {
      const k = i++ % stats.length;
      setTurns((t) => t.map((n, j) => (j === k ? n + 1 : n)));
    }, 3500);
    return () => window.clearInterval(id);
  }, [shown]);

  return (
    <div ref={ref} className="grid grid-cols-2 gap-3 [perspective:1200px] md:gap-4">
      {stats.map((s, i) => (
        <div
          key={s.label}
          className={`flex aspect-[5/4] flex-col justify-end rounded-sm p-5 shadow-sm md:p-6 ${s.tone}`}
          style={{
            transform: shown ? `rotateY(${turns[i] * 360}deg)` : "rotateY(90deg)",
            opacity: shown ? 1 : 0,
            transition: shown
              ? `transform 0.9s cubic-bezier(0.16,1,0.3,1) ${turns[i] ? 0 : i * 0.18}s, opacity 0.4s ${i * 0.18}s`
              : "none",
          }}
        >
          <span className="font-heading text-4xl leading-none sm:text-5xl md:text-6xl">
            <CountUp value={s.value} duration={1600} />
          </span>
          <span className="btn-caps mt-2 text-xs tracking-[0.16em] opacity-80">{s.label}</span>
        </div>
      ))}
    </div>
  );
};

export default StatFlipGrid;
